/**
 * 管理后台菜单码与操作权限码常量。
 *
 * 须与后端 AdminAuthorizationCodes（Java）保持一致，
 * 用于 ProLayout 菜单过滤、{@link ../components/ActionGuard.tsx} 按钮显隐及路由守卫。
 */
export const ADMIN_MENU_CODES = {
  ONBOARDING: 'onboarding',
  ONBOARDING_APPLICATIONS: 'onboarding.applications',
  VILLAGE_OPS: 'village-ops',
  VILLAGE_OPS_OVERVIEW: 'village-ops.overview',
  VILLAGE_OPS_ACTIVITIES: 'village-ops.activities',
  VILLAGE_OPS_JOBS: 'village-ops.jobs',
  VILLAGE_OPS_PRODUCTS: 'village-ops.products',
  VILLAGE_OPS_RESOLUTIONS: 'village-ops.resolutions',
  VILLAGE_OPS_IMPRESSIONS: 'village-ops.impressions',
  VILLAGE_OPS_SHORT_VIDEOS: 'village-ops.short-videos',
  VILLAGE_OPS_PRODUCT_ORDERS: 'village-ops.product-orders',
  VILLAGE_OPS_VILLAGERS: 'village-ops.villagers',
  VILLAGE_OPS_ARTISANS: 'village-ops.artisans',
  VILLAGE_OPS_VILLAGER_APPLICATIONS: 'village-ops.villager-applications',
  VILLAGE_OPS_PROFILE: 'village-ops.profile',
  PLATFORM_GOVERNANCE: 'platform-governance',
  PLATFORM_GOVERNANCE_OVERVIEW: 'platform-governance.overview',
  PLATFORM_GOVERNANCE_USERS: 'platform-governance.users',
  PLATFORM_GOVERNANCE_VILLAGES: 'platform-governance.villages',
  PLATFORM_GOVERNANCE_BANNERS: 'platform-governance.banners',
  PLATFORM_GOVERNANCE_ANNOUNCEMENTS: 'platform-governance.announcements',
  PLATFORM_GOVERNANCE_SYSTEM_PARAMETER: 'platform-governance.system-parameter',
  PLATFORM_GOVERNANCE_MONITORING_EVENTS: 'platform-governance.monitoring-events',
  PLATFORM_GOVERNANCE_SMS_MESSAGES: 'platform-governance.sms-messages',
  PLATFORM_GOVERNANCE_HELP_FAQ: 'platform-governance.help-faq',
  PLATFORM_GOVERNANCE_FEEDBACKS: 'platform-governance.feedbacks',
  PLATFORM_GOVERNANCE_CONTENT_REPORTS: 'platform-governance.content-reports',
  PLATFORM_GOVERNANCE_SHORT_VIDEOS: 'platform-governance.short-videos',
  PLATFORM_GOVERNANCE_DICTIONARIES: 'platform-governance.dictionaries',
  PLATFORM_GOVERNANCE_ADMIN_ROLES: 'platform-governance.admin-roles',
  PLATFORM_GOVERNANCE_ADMIN_MENUS: 'platform-governance.admin-menus',
  PLATFORM_GOVERNANCE_ADMIN_PERMISSIONS: 'platform-governance.admin-permissions',
} as const;

export const ADMIN_ACTION_CODES = {
  ONBOARDING_APPLICATIONS_REVIEW: 'onboarding.applications.review',
  ONBOARDING_APPLICATIONS_UPDATE: 'onboarding.applications.update',
  ONBOARDING_APPLICATIONS_ACTIVATE: 'onboarding.applications.activate',
  ONBOARDING_APPLICATIONS_ASSIGN_ADMIN: 'onboarding.applications.assign-admin',
  PLATFORM_VILLAGES_CREATE: 'platform.villages.create',
  PLATFORM_VILLAGES_UPDATE: 'platform.villages.update',
  PLATFORM_VILLAGES_DELETE: 'platform.villages.delete',
  PLATFORM_VILLAGES_UPDATE_ADMINS: 'platform.villages.update-admins',
  PLATFORM_USERS_CREATE: 'platform.users.create',
  PLATFORM_USERS_RESET_PASSWORD: 'platform.users.reset-password',
  PLATFORM_USERS_ASSIGN_ADMIN_ROLE: 'platform.users.assign-admin-role',
  PLATFORM_ANNOUNCEMENTS_CREATE: 'platform.announcements.create',
  PLATFORM_ANNOUNCEMENTS_UPDATE: 'platform.announcements.update',
  PLATFORM_ANNOUNCEMENTS_DELETE: 'platform.announcements.delete',
  PLATFORM_BANNERS_CREATE: 'platform.banners.create',
  PLATFORM_BANNERS_UPDATE: 'platform.banners.update',
  PLATFORM_BANNERS_DELETE: 'platform.banners.delete',
  PLATFORM_BANNERS_TOGGLE: 'platform.banners.toggle',
  PLATFORM_SYSTEM_PARAMS_CREATE: 'platform.system-params.create',
  PLATFORM_SYSTEM_PARAMS_UPDATE: 'platform.system-params.update',
  PLATFORM_SYSTEM_PARAMS_DELETE: 'platform.system-params.delete',
  PLATFORM_FAQS_CREATE: 'platform.faqs.create',
  PLATFORM_FAQS_UPDATE: 'platform.faqs.update',
  PLATFORM_FAQS_DELETE: 'platform.faqs.delete',
  PLATFORM_FEEDBACKS_UPDATE: 'platform.feedbacks.update',
  PLATFORM_CONTENT_REPORTS_REVIEW: 'platform.content-reports.review',
  PLATFORM_SHORT_VIDEOS_REVIEW: 'platform.short-videos.review',
  PLATFORM_DICTIONARIES_CREATE_GROUP: 'platform.dictionaries.create-group',
  PLATFORM_DICTIONARIES_UPDATE_GROUP: 'platform.dictionaries.update-group',
  PLATFORM_DICTIONARIES_DELETE_GROUP: 'platform.dictionaries.delete-group',
  PLATFORM_DICTIONARIES_CREATE_ITEM: 'platform.dictionaries.create-item',
  PLATFORM_DICTIONARIES_UPDATE_ITEM: 'platform.dictionaries.update-item',
  PLATFORM_DICTIONARIES_DELETE_ITEM: 'platform.dictionaries.delete-item',
  PLATFORM_ADMIN_ROLES_CREATE: 'platform.admin-roles.create',
  PLATFORM_ADMIN_ROLES_UPDATE: 'platform.admin-roles.update',
  PLATFORM_ADMIN_ROLES_DELETE: 'platform.admin-roles.delete',
  PLATFORM_ADMIN_ROLES_ASSIGN_MENUS: 'platform.admin-roles.assign-menus',
  PLATFORM_ADMIN_ROLES_ASSIGN_PERMISSIONS: 'platform.admin-roles.assign-permissions',
  PLATFORM_ADMIN_MENUS_UPDATE: 'platform.admin-menus.update',
  PLATFORM_ADMIN_PERMISSIONS_UPDATE: 'platform.admin-permissions.update',
  VILLAGE_ACTIVITIES_CREATE: 'village.activities.create',
  VILLAGE_ACTIVITIES_UPDATE: 'village.activities.update',
  VILLAGE_ACTIVITIES_TOGGLE_DISPLAY: 'village.activities.toggle-display',
  VILLAGE_ACTIVITIES_SEND_REMINDER: 'village.activities.send-reminder',
  VILLAGE_JOBS_CREATE: 'village.jobs.create',
  VILLAGE_JOBS_UPDATE: 'village.jobs.update',
  VILLAGE_JOBS_TOGGLE_DISPLAY: 'village.jobs.toggle-display',
  VILLAGE_PRODUCTS_TOGGLE_DISPLAY: 'village.products.toggle-display',
  VILLAGE_PRODUCTS_UPDATE_PICKUP: 'village.products.update-pickup',
  VILLAGE_PRODUCT_ORDERS_UPDATE_STATUS: 'village.product-orders.update-status',
  VILLAGE_RESOLUTIONS_CREATE: 'village.resolutions.create',
  VILLAGE_RESOLUTIONS_UPDATE: 'village.resolutions.update',
  VILLAGE_RESOLUTIONS_VIEW_VOTES: 'village.resolutions.view-votes',
  VILLAGE_RESOLUTIONS_MODERATE_COMMENTS: 'village.resolutions.moderate-comments',
  VILLAGE_IMPRESSIONS_CREATE: 'village.impressions.create',
  VILLAGE_IMPRESSIONS_UPDATE: 'village.impressions.update',
  VILLAGE_IMPRESSIONS_REVIEW: 'village.impressions.review',
  VILLAGE_SHORT_VIDEOS_CREATE: 'village.short-videos.create',
  VILLAGE_SHORT_VIDEOS_UPDATE: 'village.short-videos.update',
  VILLAGE_SHORT_VIDEOS_REVIEW: 'village.short-videos.review',
  VILLAGE_PROFILE_UPDATE: 'village.profile.update',
  VILLAGE_VILLAGERS_CREATE: 'village.villagers.create',
  VILLAGE_VILLAGERS_UPDATE: 'village.villagers.update',
  VILLAGE_VILLAGERS_DELETE: 'village.villagers.delete',
  VILLAGE_ARTISANS_CREATE: 'village.artisans.create',
  VILLAGE_ARTISANS_UPDATE: 'village.artisans.update',
  VILLAGE_ARTISANS_TOGGLE_DISPLAY: 'village.artisans.toggle-display',
  VILLAGE_ARTISANS_DELETE: 'village.artisans.delete',
  VILLAGE_VILLAGER_APPLICATIONS_REVIEW: 'village.villager-applications.review',
} as const;

export const ADMIN_ROUTE_MENU_CODES: Record<string, string> = {
  '/onboarding/applications': ADMIN_MENU_CODES.ONBOARDING_APPLICATIONS,
  '/village-ops': ADMIN_MENU_CODES.VILLAGE_OPS_OVERVIEW,
  '/village-ops/activities': ADMIN_MENU_CODES.VILLAGE_OPS_ACTIVITIES,
  '/village-ops/jobs': ADMIN_MENU_CODES.VILLAGE_OPS_JOBS,
  '/village-ops/products': ADMIN_MENU_CODES.VILLAGE_OPS_PRODUCTS,
  '/village-ops/resolutions': ADMIN_MENU_CODES.VILLAGE_OPS_RESOLUTIONS,
  '/village-ops/impressions': ADMIN_MENU_CODES.VILLAGE_OPS_IMPRESSIONS,
  '/village-ops/short-videos': ADMIN_MENU_CODES.VILLAGE_OPS_SHORT_VIDEOS,
  '/village-ops/product-orders': ADMIN_MENU_CODES.VILLAGE_OPS_PRODUCT_ORDERS,
  '/village-ops/villagers': ADMIN_MENU_CODES.VILLAGE_OPS_VILLAGERS,
  '/village-ops/artisans': ADMIN_MENU_CODES.VILLAGE_OPS_ARTISANS,
  '/village-ops/villager-applications': ADMIN_MENU_CODES.VILLAGE_OPS_VILLAGER_APPLICATIONS,
  '/village-ops/profile': ADMIN_MENU_CODES.VILLAGE_OPS_PROFILE,
  '/platform-governance': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_OVERVIEW,
  '/platform-governance/users': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_USERS,
  '/platform-governance/villages': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_VILLAGES,
  '/platform-governance/banners': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_BANNERS,
  '/platform-governance/announcements': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_ANNOUNCEMENTS,
  '/platform-governance/system-parameter': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_SYSTEM_PARAMETER,
  '/platform-governance/category-configs': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_SYSTEM_PARAMETER,
  '/platform-governance/monitoring-events': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_MONITORING_EVENTS,
  '/platform-governance/sms-messages': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_SMS_MESSAGES,
  '/platform-governance/help-faq': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_HELP_FAQ,
  '/platform-governance/feedbacks': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_FEEDBACKS,
  '/platform-governance/content-reports': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_CONTENT_REPORTS,
  '/platform-governance/short-videos': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_SHORT_VIDEOS,
  '/platform-governance/dictionaries': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_DICTIONARIES,
  '/platform-governance/admin-roles': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_ADMIN_ROLES,
  '/platform-governance/admin-menus': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_ADMIN_MENUS,
  '/platform-governance/admin-permissions': ADMIN_MENU_CODES.PLATFORM_GOVERNANCE_ADMIN_PERMISSIONS,
};

const DEFAULT_ROUTE_ORDER = [
  '/onboarding/applications',
  '/village-ops',
  '/village-ops/activities',
  '/village-ops/jobs',
  '/village-ops/products',
  '/village-ops/resolutions',
  '/village-ops/impressions',
  '/village-ops/short-videos',
  '/village-ops/product-orders',
  '/village-ops/villagers',
  '/village-ops/artisans',
  '/village-ops/villager-applications',
  '/village-ops/profile',
  '/platform-governance',
  '/platform-governance/users',
  '/platform-governance/villages',
  '/platform-governance/banners',
  '/platform-governance/announcements',
  '/platform-governance/system-parameter',
  '/platform-governance/category-configs',
  '/platform-governance/monitoring-events',
  '/platform-governance/sms-messages',
  '/platform-governance/help-faq',
  '/platform-governance/feedbacks',
  '/platform-governance/content-reports',
  '/platform-governance/short-videos',
  '/platform-governance/dictionaries',
  '/platform-governance/admin-roles',
  '/platform-governance/admin-menus',
  '/platform-governance/admin-permissions',
] as const;

/** 规范化路径用于菜单码查找（去掉尾部斜杠）。 */
export const normalizeRoutePath = (path?: string) => {
  if (!path) {
    return '/';
  }
  if (path === '/') {
    return '/';
  }
  return path.replace(/\/+$/, '') || '/';
};

/** 未传 menuCode 时视为允许（用于可选门禁）。 */
export const hasMenuPermission = (
  currentUser: API.CurrentUser | undefined,
  menuCode?: string,
) => {
  if (!menuCode) {
    return true;
  }
  return Boolean(currentUser?.menuCodes?.includes(menuCode));
};

/** 未传 actionCode 时视为允许。 */
export const hasActionPermission = (
  currentUser: API.CurrentUser | undefined,
  actionCode?: string,
) => {
  if (!actionCode) {
    return true;
  }
  return Boolean(currentUser?.actionCodes?.includes(actionCode));
};

/** 是否具备后台入口：已绑定管理角色且至少有一个菜单或操作权限。 */
export const hasAdminConsoleAccess = (currentUser?: API.CurrentUser) => {
  if (!currentUser?.adminRoleId) {
    return false;
  }
  return Boolean(
    (currentUser.menuCodes?.length || 0) > 0 || (currentUser.actionCodes?.length || 0) > 0,
  );
};

/** 根据当前 pathname 解析应对应的菜单权限码。 */
export const getRouteMenuCode = (pathname?: string) =>
  ADMIN_ROUTE_MENU_CODES[normalizeRoutePath(pathname)];

/** 登录后跳转到用户有权访问的第一个后台路由（见 DEFAULT_ROUTE_ORDER）。 */
export const findFirstAccessiblePath = (currentUser?: API.CurrentUser) =>
  DEFAULT_ROUTE_ORDER.find((path) =>
    hasMenuPermission(currentUser, ADMIN_ROUTE_MENU_CODES[path]),
  );
